/**
 * Google One Tap sign-up for the public site.
 *
 * The prompt appears top-right for any visitor already signed into Google. Picking an
 * account posts the ID token Google issues to `/api/auth/google/`, which creates the
 * account (or matches an existing one) and unlocks the first-quote welcome discount.
 *
 * On success the visitor is sent to the portal's login page (`data-redirect-url`, which
 * is `CUSTOMER_PORTAL_LOGIN_URL`). The JWTs that come back are still not stored or passed
 * on: the portal lives on another origin and cannot read this one's localStorage, and
 * putting tokens in a redirect URL would leak them into history, logs and Referer
 * headers. Nothing about the sign-in (credential, token, email) is added to the URL.
 * Instead the portal's own One Tap (`auto_select: true` in the portal's
 * `GoogleLoginBlock.tsx`) signs them in the moment they land there, so the hand-off
 * costs them no clicks. The redirect happens only when the visitor chose an account
 * (`select_by` is not `auto`): a returning visitor who is signed in automatically is still
 * posted to the endpoint, but stays on the page they were reading. A rejected sign-in, or
 * an empty redirect URL, also leaves the visitor where they are.
 *
 * Config comes from `#google-one-tap`'s data attributes so this file needs no inline
 * script. Loaded from base.html after Google's GSI client (both deferred, so they run in
 * document order and `window.google` is ready by the time this executes).
 */
class GoogleOneTap {
    constructor(root) {
        this.root = root;
        this.clientId = root.dataset.clientId || '';
        this.endpoint = root.dataset.endpoint || '/api/auth/google/';
        this.redirectUrl = root.dataset.redirectUrl || '';
    }

    init() {
        if (!this.clientId) return;
        if (!window.google || !window.google.accounts || !window.google.accounts.id) {
            // GSI failed to load (blocked by an extension, offline, etc.). Nothing to do —
            // the site works fine without it.
            return;
        }

        window.google.accounts.id.initialize({
            client_id: this.clientId,
            callback: (response) => this.handleCredential(response),
            // A returning visitor is signed back in with no click at all. Google only
            // auto-selects where they have already granted this app access, so a
            // first-time visitor still chooses their account explicitly.
            auto_select: true,
            // An accidental click elsewhere on the page shouldn't kill the prompt.
            cancel_on_tap_outside: false,
            context: 'signup',
        });
        window.google.accounts.id.prompt();
    }

    async handleCredential(response) {
        if (!response || !response.credential) return;

        try {
            const result = await fetch(this.endpoint, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    credential: response.credential,
                    timezone: this.timezone(),
                }),
            });

            if (!result.ok) {
                // A disabled or locked account lands here. The visitor did not ask to sign
                // in on a marketing page, so nothing is shown — they can still use the
                // portal's login page, which does explain what went wrong.
                console.debug('[GoogleOneTap] Sign-in rejected:', result.status);
                return;
            }

            const payload = await result.json();
            const email = payload && payload.data && payload.data.user && payload.data.user.email;

            // Server-side already captured the sign-up itself; this joins the visitor's
            // existing anonymous session to that person on the same lower-cased email.
            if (email && typeof window.identifyUser === 'function') {
                window.identifyUser(email);
            }
            if (typeof window.trackEvent === 'function') {
                window.trackEvent('google_one_tap_signed_in');
            }

            // Hand off to the portal only when the visitor picked an account. Auto
            // sign-in fires on page load, and would yank a reader off a blog or service
            // page, or out of a half-filled /free-quote/ form, that they never asked to
            // leave. Plain URL only: no credential, token or email.
            if (this.redirectUrl && response.select_by !== 'auto') {
                window.location.assign(this.redirectUrl);
            }
        } catch (error) {
            console.debug('[GoogleOneTap] Sign-in failed:', error);
        }
    }

    timezone() {
        try {
            return Intl.DateTimeFormat().resolvedOptions().timeZone || '';
        } catch (error) {
            return '';
        }
    }
}

(function initGoogleOneTap() {
    const root = document.getElementById('google-one-tap');
    if (root) new GoogleOneTap(root).init();
})();
